import React from 'react';

/**
 * DataTable — the surface card + header + scrollable table + pager footer from
 * the design system. Composable: pass `columns` for the (optionally sortable)
 * header and render the body rows as `children`.
 *
 * columns: [{ key, label, align?: 'left'|'right', sortable?: boolean, title? }]
 *   - align 'right' marks numeric columns (right-aligned + tabular-nums).
 * Sort props (optional): sortKey, sortDir ('asc'|'desc'), onSort(key).
 * Pager props (optional): page, perPage, total, onPage, onPerPage, perPageOptions.
 *   The footer auto-hides when total <= 10 (matches the app's convention).
 *
 * Row helpers for the body cells:
 *   <td className="ui-num">  → right-aligned numeric cell
 *   <tr className="ui-row--discrepancy"> → danger-soft discrepancy row
 */
export default function DataTable({
  title,
  tools,
  columns,
  sortKey,
  sortDir,
  onSort,
  minWidth = 760,
  page,
  perPage,
  total,
  onPage,
  onPerPage,
  perPageOptions = [10, 25, 50],
  children,
}) {
  const hasPager = typeof total === 'number' && total > 10;
  const start = total === 0 ? 0 : page * perPage + 1;
  const end = Math.min((page + 1) * perPage, total);

  return (
    <div className="ui-tablecard">
      {(title || tools) && (
        <div className="ui-tablecard__head">
          {title && <h2 className="ui-tablecard__title">{title}</h2>}
          {tools && <div className="ui-tablecard__tools">{tools}</div>}
        </div>
      )}

      <div className="ui-tablecard__scroll">
        <table className="ui-table" style={{ minWidth }}>
          <thead>
            <tr>
              {columns.map(col => {
                const isNum = col.align === 'right';
                const sortable = col.sortable && onSort;
                const active = sortable && col.key === sortKey;
                return (
                  <th
                    key={col.key || col.label}
                    title={col.title}
                    className={[isNum ? 'ui-num' : '', sortable ? 'ui-sortable' : ''].filter(Boolean).join(' ')}
                    onClick={sortable ? () => onSort(col.key) : undefined}
                  >
                    {col.label}
                    {sortable && (
                      <span className={`ui-sort-icon${active ? ' ui-sort-active' : ''}`}>
                        {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>

      {hasPager && (
        <div className="ui-tablecard__foot">
          {onPerPage && (
            <label className="ui-pager-perpage">
              Filas por página:
              <select value={perPage} onChange={e => onPerPage(Number(e.target.value))}>
                {perPageOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          )}
          <span className="ui-pager-info">{start}–{end} de {total}</span>
          <div className="ui-pager-btns">
            <button className="ui-pager-btn" onClick={() => onPage(page - 1)} disabled={page === 0}>‹</button>
            <button className="ui-pager-btn" onClick={() => onPage(page + 1)} disabled={end >= total}>›</button>
          </div>
        </div>
      )}
    </div>
  );
}
